import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { tokensAPI } from "../services/api";
import { useTranslation } from "react-i18next";
import {
  Key,
  Plus,
  Eye,
  EyeOff,
  Copy,
  Trash2,
  Activity,
  Calendar,
  Settings,
  AlertCircle,
  Shield,
  Lock,
} from "lucide-react";
import toast from "react-hot-toast";

const EXPIRATION_PRESETS = {
  never: null,
  days90: 90,
  year1: 365,
  custom: "custom",
};

const Tokens = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTokenValue, setShowTokenValue] = useState({});
  const [selectedToken, setSelectedToken] = useState(null);
  const [expirationMode, setExpirationMode] = useState("never");
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm();

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const data = await tokensAPI.list();
      setTokens(data.tokens || []);
    } catch (error) {
      console.error("Failed to fetch tokens:", error);
    } finally {
      setLoading(false);
    }
  };

  const onCreateToken = async (data) => {
    try {
      let expiresAt;

      if (expirationMode === "custom") {
        expiresAt = data.expiresAt || undefined;
      } else {
        const presetDays = EXPIRATION_PRESETS[expirationMode];
        expiresAt =
          typeof presetDays === "number"
            ? new Date(Date.now() + presetDays * 24 * 60 * 60 * 1000).toISOString()
            : undefined;
      }

      const result = await tokensAPI.create({
        ...data,
        expiresAt,
        rateLimitPerMinute: parseInt(data.rateLimitPerMinute),
        rateLimitPerHour: parseInt(data.rateLimitPerHour),
        rateLimitPerDay: parseInt(data.rateLimitPerDay),
        permissions: [],
        allowedIPs: data.allowedIPs
          ? data.allowedIPs.split(",").map((ip) => ip.trim())
          : [],
      });

      toast.success(t("tokens.createSuccess"));
      setShowCreateModal(false);
      setSelectedToken(result.token);
      setExpirationMode("never");
      reset();
      fetchTokens();
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const toggleTokenStatus = async (tokenId, isActive) => {
    try {
      await tokensAPI.update(tokenId, { isActive: !isActive });
      toast.success(
        `Token ${!isActive ? t("tokens.activate") : t("tokens.deactivate")}d`,
      );
      fetchTokens();
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const deleteToken = async (tokenId) => {
    if (!confirm(t("tokens.deleteConfirm"))) return;

    try {
      await tokensAPI.delete(tokenId);
      toast.success(t("tokens.deleteSuccess"));
      fetchTokens();
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const copyToClipboard = (text = "") => {
    if (!text) {
      toast.error(t("common.nothingToCopy"));
      return;
    }
    navigator.clipboard.writeText(text);
    toast.success("Copie dans le presse-papiers");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-CH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return t("tokens.never");
    const target = new Date(dateString).getTime();
    const now = Date.now();
    const diff = target - now;
    const absDiff = Math.abs(diff);
    const units = [
      { key: "day", ms: 1000 * 60 * 60 * 24 },
      { key: "hour", ms: 1000 * 60 * 60 },
      { key: "minute", ms: 1000 * 60 },
    ];

    for (const unit of units) {
      if (absDiff >= unit.ms) {
        const value = Math.round(diff / unit.ms);
        const count = Math.abs(value);
        return value >= 0
          ? t("tokens.relativeFromNow", {
              count,
              unit: t(`tokens.timeUnit.${unit.key}`, { count }),
            })
          : t("tokens.relativeAgo", {
              count,
              unit: t(`tokens.timeUnit.${unit.key}`, { count }),
            });
      }
    }
    return diff >= 0 ? t("tokens.relativeSoon") : t("tokens.relativeJustNow");
  };

  const activeCount = tokens.filter((token) => token.isActive).length;
  const strictAccessCount = tokens.filter(
    (token) => (token.allowedIPs?.length || 0) > 0,
  ).length;
  const totalUsage = tokens.reduce(
    (acc, token) => acc + (token.usageCount || 0),
    0,
  );
  const now = Date.now();
  const thirtyDaysFromNow = now + 30 * 24 * 60 * 60 * 1000;
  const nextExpiration = [...tokens]
    .filter((token) => {
      if (!token.expiresAt) return false;
      const expiry = new Date(token.expiresAt).getTime();
      return expiry > now && expiry <= thirtyDaysFromNow;
    })
    .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt))[0];
  const displayedTokenValue = (token) => {
    if (!token) return "";
    if (showTokenValue[token.id] && token.token) {
      return token.token;
    }
    if (token.prefix) {
      return `${token.prefix}••••`;
    }
    if (token.token) {
      return `${token.token.slice(0, 6)}••••`;
    }
    return "Secret";
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-[#020617] text-white flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030816] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-10 right-0 h-80 w-80 rounded-full bg-cyan-500/20 blur-[140px]" />
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-indigo-500/20 blur-[140px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full [background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.12)_1px,_transparent_0)] [background-size:34px_34px]" />
        </div>
      </div>

      <div className="relative z-10 px-4 pt-6 pb-10 sm:px-8 lg:px-12 space-y-8">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl shadow-cyan-500/10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-cyan-100/80">
                <Shield className="h-4 w-4 text-cyan-300" />
                {t("tokens.credentialVault")}
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-white">
                {t("tokens.title")}
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                {t("tokens.subtitle")}
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-5 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/30 transition hover:shadow-cyan-400/40"
            >
              <Plus className="h-5 w-5" />
              {t("tokens.createToken")}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("tokens.active")}
              </p>
              <p className="mt-3 text-3xl font-semibold">{activeCount}</p>
              <p className="text-sm text-slate-400">
                {t("tokens.ofTotal", "of {{count}} total keys", {
                  count: tokens.length,
                })}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("tokens.requests")}
              </p>
              <p className="mt-3 text-3xl font-semibold">{totalUsage}</p>
              <p className="text-sm text-slate-400">
                {t("tokens.callsRecorded")}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("tokens.hardened")}
              </p>
              <p className="mt-3 text-3xl font-semibold">{strictAccessCount}</p>
              <p className="text-sm text-slate-400">
                {t("tokens.scopedAccess")}
              </p>
            </div>
          </div>
        </div>

        {selectedToken && (
          <div className="relative overflow-hidden rounded-3xl border border-cyan-500/30 bg-cyan-500/10 p-6 text-white shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-indigo-500/30" />
            <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm uppercase tracking-[0.4em] text-cyan-100/80">
                  {t("tokens.newSecret")}
                </p>
                <h3 className="mt-2 text-2xl font-semibold">
                  {t("tokens.tokenCreated")}
                </h3>
                <p className="mt-1 text-sm text-slate-100">
                  {t(
                    "tokens.copyNow",
                    "Copiez cette valeur maintenant. Elle disparaitra apres la fermeture du message.",
                  )}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <code className="rounded-2xl bg-slate-950/60 px-4 py-3 text-sm font-mono">
                    {selectedToken.token}
                  </code>
                  <button
                    onClick={() => copyToClipboard(selectedToken.token)}
                    className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-medium"
                  >
                    <Copy className="h-4 w-4" />
                    {t("common.copy")}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setSelectedToken(null)}
                className="self-start rounded-full border border-white/30 px-4 py-2 text-sm font-medium"
              >
                {t("common.dismiss")}
              </button>
            </div>
          </div>
        )}

        {nextExpiration && (
          <div className="rounded-3xl border border-amber-400/30 bg-amber-400/10 p-5 text-amber-50">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5" />
              <div>
                <p className="text-sm uppercase tracking-[0.4em]">
                  {t("tokens.upcomingExpiry")}
                </p>
                <p className="mt-1 text-base">
                  <span className="font-semibold">{nextExpiration.name}</span>{" "}
                  {t("tokens.expiresOn", {
                    date: formatDate(nextExpiration.expiresAt),
                    relative: formatRelativeTime(nextExpiration.expiresAt),
                  })}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-3xl border border-white/10 bg-slate-950/60 p-6 shadow-2xl shadow-black/40">
          {tokens.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-white/20 bg-white/5">
                <Key className="h-8 w-8 text-white/70" />
              </div>
              <h3 className="mt-6 text-2xl font-semibold">
                {t("tokens.noTokens")}
              </h3>
              <p className="mt-2 max-w-md text-sm text-slate-300">
                {t("tokens.noTokensDesc")}
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-5 py-3 text-base font-semibold text-white"
              >
                <Plus className="h-5 w-5" />
                {t("tokens.createToken")}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {tokens.map((token) => (
                <div
                  key={token.id}
                  className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur"
                >
                  <div className="absolute inset-0 opacity-10">
                    <div className="h-full w-full [background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.3)_1px,_transparent_0)] [background-size:24px_24px]" />
                  </div>
                  <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400/80 to-indigo-500/80 text-white">
                          <Key className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-lg font-semibold">{token.name}</p>
                          <p className="text-sm text-slate-300 flex items-center gap-2">
                            <span className="font-mono text-base tracking-wide">
                              {displayedTokenValue(token)}
                            </span>
                            {token.token && (
                              <button
                                onClick={() =>
                                  setShowTokenValue((prev) => ({
                                    ...prev,
                                    [token.id]: !prev[token.id],
                                  }))
                                }
                                className="rounded-full border border-white/20 p-1 text-white/70 hover:text-white"
                                title={
                                  showTokenValue[token.id]
                                    ? t("tokens.hideToken")
                                    : t("tokens.showToken")
                                }
                              >
                                {showTokenValue[token.id] ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() =>
                                copyToClipboard(token.token || token.prefix)
                              }
                              className="rounded-full border border-white/20 p-1 text-white/70 hover:text-white"
                              title={t("tokens.copyToken")}
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.3em] text-slate-400">
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs normal-case">
                          <Calendar className="h-3.5 w-3.5" />{" "}
                          {t("tokens.createdAt")} {formatDate(token.createdAt)}
                        </span>
                        {token.lastUsedAt && (
                          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs normal-case">
                            <Activity className="h-3.5 w-3.5" />{" "}
                            {t("tokens.lastUsed")}{" "}
                            {formatRelativeTime(token.lastUsedAt)}
                          </span>
                        )}
                        {!token.lastUsedAt && (
                          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs normal-case text-amber-200">
                            <AlertCircle className="h-3.5 w-3.5" />{" "}
                            {t("tokens.neverUsed")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start">
                      <button
                        onClick={() =>
                          toggleTokenStatus(token.id, token.isActive)
                        }
                        className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
                          token.isActive
                            ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                            : "border-white/20 bg-white/5 text-white"
                        }`}
                      >
                        {token.isActive
                          ? t("tokens.deactivate")
                          : t("tokens.activate")}
                      </button>
                      <button
                        onClick={() => deleteToken(token.id)}
                        className="inline-flex items-center justify-center rounded-2xl border border-white/20 bg-white/5 p-2 text-white/70 hover:text-white"
                        title="Delete token"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="relative z-10 mt-6 grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                    <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-4">
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                        {t("tokens.rateLimits")}
                      </p>
                      <p className="mt-2 text-base text-white">
                        {token.rateLimitPerMinute}/min ·{" "}
                        {token.rateLimitPerHour}/hr
                      </p>
                      <p className="text-xs text-slate-400">
                        {token.rateLimitPerDay}
                        {t("tokens.dayCap")}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-4">
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                        {t("tokens.usageLabel")}
                      </p>
                      <p className="mt-2 text-2xl font-semibold">
                        {token.usageCount || 0}
                        <span className="ml-1 text-sm font-normal text-slate-400">
                          {t("tokens.calls")}
                        </span>
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-4">
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                        {t("tokens.network")}
                      </p>
                      <p className="mt-2 text-sm text-slate-200">
                        {token.allowedIPs?.length
                          ? token.allowedIPs.join(", ")
                          : t("tokens.allIPs")}
                      </p>
                    </div>
                  </div>

                  {token.expiresAt && (
                    <div className="relative z-10 mt-4 inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs text-slate-200">
                      <Lock className="h-3.5 w-3.5" />
                      {t("tokens.expiresLabel", {
                        date: formatDate(token.expiresAt),
                        relative: formatRelativeTime(token.expiresAt),
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {showCreateModal && (
          <div className="overlay-backdrop fixed inset-0 z-50 flex items-start justify-center bg-slate-950/80 px-4 py-4 backdrop-blur sm:items-center">
            <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-slate-900/95 shadow-2xl">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500" />
              <div className="relative z-10 flex flex-col">
                <div className="flex items-start justify-between border-b border-white/10 px-6 pb-3 pt-5">
                  <div>
                    <h3 className="text-2xl font-semibold text-white">
                      {t("tokens.modal.createTitle")}
                    </h3>
                  </div>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      reset();
                    }}
                    className="rounded-full border border-white/20 px-3 py-1 text-sm text-white/70 hover:text-white"
                  >
                    {t("common.close")}
                  </button>
                </div>

                <form
                  onSubmit={handleSubmit(onCreateToken)}
                  className="flex flex-col"
                >
                  <div className="max-h-[68vh] overflow-y-auto px-6 py-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-200">
                          {t("tokens.tokenName")}
                        </label>
                        <input
                          {...register("name", {
                            required: "Le nom du token est requis",
                          })}
                          type="text"
                          className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
                          placeholder="eg. Production ingest"
                        />
                        {errors.name && (
                          <p className="mt-2 text-sm text-rose-300">
                            {errors.name.message}
                          </p>
                        )}
                      </div>

                      <div className="grid gap-4 sm:grid-cols-3">
                        {[
                          "rateLimitPerMinute",
                          "rateLimitPerHour",
                          "rateLimitPerDay",
                        ].map((field, idx) => (
                          <div key={field}>
                            <label className="text-sm font-medium text-slate-200">
                              {idx === 0
                                ? t("tokens.perMinute")
                                : idx === 1
                                  ? t("tokens.perHour")
                                  : t("tokens.perDay")}
                            </label>
                            <input
                              {...register(field, {
                                required: "La limite est requise",
                                min: { value: 1, message: "Minimum 1" },
                              })}
                              type="number"
                              className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
                              placeholder={
                                idx === 0 ? "60" : idx === 1 ? "1000" : "10000"
                              }
                              defaultValue={
                                idx === 0 ? "60" : idx === 1 ? "1000" : "10000"
                              }
                            />
                          </div>
                        ))}
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="text-sm font-medium text-slate-200">
                            {t("tokens.allowedIPs")} (
                            {t("tokens.commaSeparated")})
                          </label>
                          <input
                            {...register("allowedIPs")}
                            type="text"
                            className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
                            placeholder="192.168.1.10, 10.0.0.0/24"
                          />
                          <p className="mt-1 text-xs text-slate-400">
                            {t(
                              "tokens.allowedIPsHint",
                              "Laissez vide pour autoriser tous les reseaux.",
                            )}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-200">
                            {t("tokens.expirationMode")}
                          </label>
                          <select
                            value={expirationMode}
                            onChange={(e) => setExpirationMode(e.target.value)}
                            className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white focus:border-cyan-400/60 focus:outline-none"
                          >
                            <option value="never">{t("tokens.expirationNever")}</option>
                            <option value="days90">{t("tokens.expiration90Days")}</option>
                            <option value="year1">{t("tokens.expiration1Year")}</option>
                            <option value="custom">{t("tokens.expirationCustom")}</option>
                          </select>
                          <p className="mt-1 text-xs text-slate-400">
                            {t(
                              "tokens.expirationModeHint",
                              "Pour une integration client stable, le mode Jamais est le plus confortable.",
                            )}
                          </p>
                        </div>
                        {expirationMode === "custom" && (
                          <div className="md:col-span-2">
                            <label className="text-sm font-medium text-slate-200">
                              {t("tokens.expirationDate")} (
                              {t("common.optional")})
                            </label>
                            <input
                              {...register("expiresAt")}
                              type="datetime-local"
                              className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
                            />
                          </div>
                        )}
                      </div>

                      <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-3 text-sm text-slate-300">
                        <div className="flex items-center gap-2 text-slate-200">
                          <Settings className="h-4 w-4" />
                          {t("tokens.advancedGuidance")}
                        </div>
                        <p className="mt-2 text-xs leading-5 text-slate-300">
                          {t(
                            "tokens.advancedGuidanceText",
                            "Production: limitez les IPs, creez un token par integration, utilisez 'Jamais' uniquement pour une integration stable, et ajustez les limites minute/heure/jour selon le volume.",
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-white/10 bg-slate-900/90 px-6 py-4 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        setExpirationMode("never");
                        reset();
                      }}
                      className="inline-flex items-center justify-center rounded-2xl border border-white/20 px-5 py-3 text-sm font-semibold text-white/80 hover:text-white"
                    >
                      {t("common.cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-cyan-500/30 disabled:opacity-60"
                    >
                      {isSubmitting ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      ) : (
                        <Key className="h-4 w-4" />
                      )}
                      {t("tokens.createToken")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tokens;
